import {
  Activation,
  ActivationStatus,
  ActivationsResponse,
  ActivationResponse,
  ActivationSummary,
  ActivationPagination,
  CreateActivationRequest,
  UpdateActivationRequest,
  ActivationPerformance,
  ActivationPerformanceResponse
} from '../types/activation'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class ActivationService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all activations with optional filtering and pagination
   * @param customerId - Customer ID (required)
   * @param search - Optional search query (searches activation name)
   * @param partnerId - Optional filter by partner ID
   * @param activationTypeId - Optional filter by activation type ID
   * @param status - Optional filter by status
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   */
  async getActivations(
    customerId: string,
    search?: string,
    partnerId?: string,
    activationTypeId?: string,
    status?: ActivationStatus,
    page?: number,
    limit?: number
  ): Promise<{ data: Activation[]; summary: ActivationSummary; pagination: ActivationPagination }> {
    const url = new URL(`${this.baseURL}/activations`)

    // Always include customerId
    url.searchParams.append('customerId', customerId)

    // Add optional filters
    if (search !== undefined && search.trim()) {
      url.searchParams.append('search', search.trim())
    }
    if (partnerId) {
      url.searchParams.append('partner_id', partnerId)
    }
    if (activationTypeId) {
      url.searchParams.append('activation_type_id', activationTypeId)
    }
    if (status) {
      url.searchParams.append('status', status)
    }
    if (page !== undefined) {
      url.searchParams.append('page', String(page))
    }
    if (limit !== undefined) {
      url.searchParams.append('limit', String(limit))
    }

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationsResponse>(response)

    return {
      data: responseData.data,
      summary: responseData.summary,
      pagination: responseData.pagination
    }
  }

  /**
   * Get a single activation by ID
   * @param id - Activation ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   */
  async getActivation(id: string, customerId: string): Promise<Activation> {
    const url = new URL(`${this.baseURL}/activations/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationResponse>(response)

    return responseData.data
  }

  /**
   * Create a new activation
   * @param customerId - Customer ID (required)
   * @param data - Activation data
   */
  async createActivation(customerId: string, data: CreateActivationRequest): Promise<Activation> {
    const url = new URL(`${this.baseURL}/activations`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationResponse>(response)

    return responseData.data
  }

  /**
   * Update an existing activation
   * @param id - Activation ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   * @param data - Update data
   */
  async updateActivation(
    id: string,
    customerId: string,
    data: UpdateActivationRequest
  ): Promise<Activation> {
    const url = new URL(`${this.baseURL}/activations/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationResponse>(response)

    return responseData.data
  }

  /**
   * Delete an activation
   * @param id - Activation ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   */
  async deleteActivation(id: string, customerId: string): Promise<void> {
    const url = new URL(`${this.baseURL}/activations/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    await Promise.race([fetch(url.toString(), config), timeoutPromise])
  }

  /**
   * Set (replace all) assets for an activation
   * @param id - Activation ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   * @param assetIds - Array of asset IDs (replaces all existing)
   */
  async setActivationAssets(
    id: string,
    customerId: string,
    assetIds: string[]
  ): Promise<Activation> {
    const url = new URL(`${this.baseURL}/activations/${id}/assets`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ asset_ids: assetIds })
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationResponse>(response)

    return responseData.data
  }

  /**
   * Remove an asset from an activation
   * @param id - Activation ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   * @param assetId - Asset ID to remove
   */
  async removeActivationAsset(
    id: string,
    customerId: string,
    assetId: string
  ): Promise<void> {
    const url = new URL(`${this.baseURL}/activations/${id}/assets/${assetId}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    await Promise.race([fetch(url.toString(), config), timeoutPromise])
  }

  /**
   * Get full performance data for an activation
   * @param id - Activation ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   */
  async getActivationPerformance(id: string, customerId: string): Promise<ActivationPerformance> {
    const url = new URL(`${this.baseURL}/activations/${id}/performance`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationPerformanceResponse>(response)

    return responseData.data
  }
}

export const activationService = new ActivationService()
